import { useCallback, useEffect, useRef, useState } from 'react';
import { eventBus, APP_EVENT_KEYS } from '@/infrastructure/eventBus';
import { useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import { ContentListComponent } from '@/presentation/feature/content/list/ContentListComponent';
import type { VirtualListHandle } from '@/presentation/components/common/virtualList/VirtualList';
import { AnimatedLoadingIndicator } from '@/presentation/components/common/loadingIndicators/LoadingIndicator';
import { ExpauseAppBar } from '@/presentation/components/common/appBar/ExpauseAppBar';
import { ContentPageEmptyState } from '@/presentation/feature/content/components/ContentPageEmptyState';
import { ContentPageErrorCard } from '@/presentation/feature/content/components/ContentPageErrorCard';
import { ContentPageFirstLoad } from '@/presentation/feature/content/components/ContentPageFirstLoad';
import { Navigator } from '@/presentation/routing/Navigator';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useMeasuredHeight } from '@/presentation/hooks/useMeasuredHeight';
import { useVideoManagement } from '@/presentation/feature/content/hooks/useVideoManagement';
import { useUserMedias } from '../hooks/useUserMedia';
import type { RootState } from '@/presentation/store';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { qaAttr } from '@/presentation/utils/qaAttr';

interface LocationState {
    medias?: UserMediaEntity[];
    currentIndex?: number;
}

/**
 * User Videos Page - Displays user's videos in a vertical list
 * 
 * Two modes:
 * 1. With initial medias + currentIndex: Shows provided medias and scrolls to currentIndex
 * 2. Without initial data: Fetches medias using useUserMedias hook
 * 
 * Supports infinite scroll for loading more videos.
 *
 * App-bar provenance: the shared `ExpauseAppBar`
 * (`presentation/components/common/appBar/ExpauseAppBar.tsx`) ports the pushed
 * content-list screen's bar
 * (`lib/presentation/screens/content/custom_widgets/scrollable/content_list_widget.dart:1112-1147`),
 * which Flutter renders here because this page's push
 * (`lib/presentation/custom_widgets/grid_widget.dart:203-214`) passes **no**
 * `showAppBar`, so the `bool showAppBar = true` default applies
 * (`lib/services/navigation_service.dart:607`, `:668`); only the two home-tab
 * pushes (`feed_content_screen.dart:353`, `exclusive_content_screen.dart:284`)
 * and the portrait full-screen push (`navigation_service.dart:2832`, inside
 * `navigateToContentFullScreen` at `:2809`) opt out. The title is
 * **intentionally absent**: Flutter's title is an empty `Container()`
 * (`content_list_widget.dart:1126`), so the bar carries the back chevron alone. The bar renders in BOTH branches —
 * the first-load spinner branch included — because Flutter keeps the same bar
 * over its empty/first-load body (`content_list_widget.dart:1029-1048`, loading
 * body at `:1071-1078`) and because a route with no back control strands a
 * deep-linked visitor (`workflow/lessons.md`). The bar is in flow and `sticky`,
 * so content sits below it and scrolls under it — a deliberate web deviation
 * from Flutter's `extendBodyBehindAppBar: true`
 * (`content_list_widget.dart:1109`), which starts the body behind the bar.
 *
 * Back control — DOCUMENTED, APPROVED DIVERGENCE (settled; do not re-raise):
 * this page's back control pops the route directly and does **not** close open
 * sheets first. Flutter's handler
 * (`lib/presentation/screens/content/custom_widgets/scrollable/content_list_screen.dart:177-190`)
 * does: it closes the comments sheet, then pops the topmost sheet, and only
 * reaches `NavigationService.pop()` when no sheet is open. On the web a browser
 * back press pops the page together with its open sheets — the established,
 * approved behaviour of the web modal/sheet system — and that same ruling was
 * deliberately extended to the in-app app-bar chevron so BOTH back affordances
 * on this page behave identically. Flutter has no such split (its chevron and
 * the system back gesture share one handler); porting the sheet-dismiss-first
 * branches to the chevron alone would give this page two different back
 * behaviours, which is why it is intentionally not done. This is a product
 * decision, not a technical limitation.
 *
 * Architecture: Component → Hook → UseCase → Service
 */
export const UserVideosPage = () => {
    const { userId } = useParams<{ userId: string }>();
    const location = useLocation();
    const { theme } = useTheme();
    const { l10n } = useLocale();
    const responsive = useResponsiveDesign();
    const currentUser = useSelector((state: RootState) => state.auth.user);
    const currentUserId = currentUser?.firebaseId;

    // Extract initial data from location state
    const locationState = location.state as LocationState | undefined;
    const initialMedias = locationState?.medias;
    const initialIndex = locationState?.currentIndex;

    // Get navigation screen key from RouteStack context
    // This key is stable for this route instance and used for both navigation and video management
    // IMPORTANT: Capture the screen key ONCE on mount and never change it
    // Initialize the ref with a value IMMEDIATELY to avoid undefined on first render
    const { currentScreenKeyRef } = useKeepAlive();
    const screenKeyRef = useRef<string>(
        currentScreenKeyRef.current || generateScreenKey(location.pathname)
    );
    const screenKey = screenKeyRef.current;

    // Log on first render only
    const hasLoggedRef = useRef(false);
    if (!hasLoggedRef.current) {
        // console.log('🔑 [UserVideosPage] Captured screenKey on mount:', screenKey);
        hasLoggedRef.current = true;
    }

    // Use feature hook for fetching medias (only if no initial data provided)
    const {
        medias: fetchedMedias,
        isLoading,
        isLoadingMore,
        hasMore,
        error,
        loadMore,
        fetchMedias,
        refresh
    } = useUserMedias(userId, !!currentUserId && userId === currentUserId);

    // State to manage medias (either from location state or fetched)
    const [medias, setMedias] = useState<UserMediaEntity[]>(initialMedias || []);
    const [isInitialized, setIsInitialized] = useState(false);

    const { autoTogglePlayPauseActiveVideo } = useVideoManagement();
    const virtualListRef = useRef<VirtualListHandle>(null);
    const [isPageActive, setIsPageActive] = useState(true);

    // Rendered height of the sticky app bar, fed to the list as
    // `scrollOffsetTop`. This page's primary entry is "tap video N in the
    // profile grid", which arrives with `currentIndex` → `initialIndex`, and
    // that landing otherwise parks the target's top edge 16px below the TOP OF
    // THE WINDOW — i.e. behind the bar, hiding the tapped video's author row
    // (`ContentComponent` renders `UserInfoComponent` first). Measured, not
    // recomputed: the bar's height is `calc(topBar.height + safeArea.top)` and
    // `safeArea.top` is an `env()` string, not a number. Declared ABOVE the
    // first-load early return (Rules of Hooks) and handed to BOTH branches —
    // forwarded through `ContentPageFirstLoad` to the same `ExpauseAppBar` root
    // the loaded branch measures — so the callback ref re-attaches itself when
    // the loading branch swaps to the loaded one and the height stays correct
    // across that swap and across an orientation flip.
    const [appBarHeight, appBarRef] = useMeasuredHeight();

    // Keep the locally displayed medias in sync with global events.
    // These listeners run alongside the identical ones in useUserMedias. Both are needed:
    // - useUserMedias listeners keep `fetchedMedias` up to date (source of truth after fetch).
    // - Page listeners are required for "initialMedias" mode: when medias are passed via
    //   location state, useUserMedias never populates `fetchedMedias`, so its internal
    //   listeners have no effect on the displayed list. The page must own these handlers
    //   so events are reflected regardless of which mode the page is operating in.
    useEffect(() => {
        return eventBus.on(APP_EVENT_KEYS.CONTENT_LIST_ITEM_REMOVED, ({ mediaId }) => {
            setMedias(prev => prev.filter(m => m.id !== mediaId));
        });
    }, []);

    useEffect(() => {
        return eventBus.on(APP_EVENT_KEYS.CONTENT_ITEM_LIKES_UPDATED, ({ mediaId, likes }) => {
            setMedias(prev => prev.map(m => m.id === mediaId ? { ...m, likes } : m));
        });
    }, []);

    useEffect(() => {
        return eventBus.on(APP_EVENT_KEYS.CONTENT_ITEM_COMMENTS_UPDATED, ({ mediaId, comments }) => {
            setMedias(prev => prev.map(m => m.id === mediaId ? { ...m, comments } : m));
        });
    }, []);

    useEffect(() => {
        return eventBus.on(APP_EVENT_KEYS.CONTENT_ITEM_UPDATED, ({ mediaId, patch }) => {
            setMedias(prev => prev.map(m => m.id === mediaId ? { ...m, ...patch } : m));
        });
    }, []);

    // Base page lifecycle management (Flutter RouteObserver pattern)
    useBasePage({
        screenKey, // Pass screenKey to differentiate between multiple UserVideosPage instances
        onInit: () => {
            if (!initialMedias) {
                fetchMedias();
            }
            setIsInitialized(true);

            return () => {
                // Cleanup on unmount (rare with KeepAlive)
            };
        },
        onPush: (repushed) => {
            if (repushed) {
                autoTogglePlayPauseActiveVideo();
            }
            setIsPageActive(true);
        },
        onPopNext: () => {
            autoTogglePlayPauseActiveVideo();
            setIsPageActive(true);
        },
        onPushNext: () => {
            autoTogglePlayPauseActiveVideo();
            setIsPageActive(false);
        },
        onPop: () => {
            autoTogglePlayPauseActiveVideo();
            setIsPageActive(false);
        },
        onDispose: () => {
            // Final cleanup (rare with KeepAlive caching)
        },
        debug: false,
    });

    // Update medias when fetched from hook (only if no initial medias)
    useEffect(() => {
        if (!initialMedias && fetchedMedias.length > 0) {
            setMedias(fetchedMedias);
        }
    }, [fetchedMedias, initialMedias]);


    const handleRefresh = () => {
        if (initialMedias) {
            // If using initial medias, can't refresh from here
            return;
        }
        refresh();
    };

    // Leading (back) handler for the app bar. Declared ABOVE the first-load
    // early return so both render branches share one instance and the hook
    // order is identical in every state.
    //
    // Deep-link guard: with no in-app history to pop, fall back to the owning
    // profile (`/:userId`) — this route is always pushed from it — instead of
    // letting `goBack()` walk off-site (same pattern as DeleteAccountPage /
    // ContactPage). `userId` comes from `useParams`, so guard the fallback on a
    // non-empty value and land on the feed when it is missing.
    const handleBack = useCallback(() => {
        Navigator.goBackOr(() =>
            userId ? Navigator.navigateToUserDetails(userId) : Navigator.navigateToFeed(),
        );
    }, [userId]);

    // Show loading state only if fetching and not initialized. The shared shell
    // keeps the app bar ABOVE the spinner (never gated on `isInitialized`) so
    // the back affordance exists for the whole first fetch — see
    // `ContentPageFirstLoad` for the Flutter citation.
    if (!isInitialized && isLoading && !initialMedias) {
        return (
            <ContentPageFirstLoad
                ref={appBarRef}
                qa={{ id: 'user-videos', status: 'loading' }}
                appBarQa={{ id: 'user-videos-app-bar' }}
                leadingQa={{ id: 'user-videos-back' }}
                onLeadingTap={handleBack}
            />
        );
    }

    // Coarse lifecycle token for the page root, derived from existing state only.
    // Mirrors FeedPage: `loading` only while the initial fetch has produced no
    // items; once medias are present (from location state or a settled fetch),
    // the page is `ready`. "Loading more" (infinite scroll, medias already
    // present) must NOT report page-level `loading`.
    // In initialMedias mode the hook never calls fetchMedias, so its `isLoading`
    // stays `true` forever — keying on `medias.length` resolves to `ready` here.
    const pageStatus = error && !initialMedias
        ? 'error'
        : isLoading && medias.length === 0
            ? 'loading'
            : 'ready';

    return (
        <div
            {...qaAttr({ id: 'user-videos', status: pageStatus })}
            className="w-full">
            {/* App bar — back chevron only, no title (Flutter renders
                `title: Container()`, content_list_widget.dart:1126). Sticky and
                translucent, so the list scrolls under it. */}
            <ExpauseAppBar
                ref={appBarRef}
                rootQa={{ id: 'user-videos-app-bar' }}
                leadingQa={{ id: 'user-videos-back' }}
                onLeadingTap={handleBack}
            />

            {/* Body — keeps the list's original `py-8` vertical rhythm; the
                padding moved off the page root so the bar sits flush at the top
                (and its own safe-area inset is not double-offset). */}
            <div className="py-8">
                {/* Error State — hidden in `initialMedias` mode, where the list
                    was handed to us and the hook never fetched. */}
                {error && !initialMedias && <ContentPageErrorCard onRetry={handleRefresh} />}

                {/* Video List */}
                <ContentListComponent
                    qa={{ id: 'user-videos-list' }}
                    screenKey={screenKey}
                    ref={virtualListRef}
                    isActive={isPageActive}
                    medias={medias}
                    currentUserId={currentUserId}
                    layout="list"
                    columns={1}
                    autoplay={true}
                    showFullscreenButton={true}
                    showMore={true}
                    muted={false}
                    isLoading={isLoading}
                    hasMore={hasMore}
                    sheetContainer={null}
                    initialIndex={initialIndex}
                    // Keeps the `initialIndex` landing (and any later
                    // `scrollToIndex`) below the sticky bar instead of behind it.
                    scrollOffsetTop={appBarHeight}
                    gap={responsive.gap['6xl']}
                    onReachedEnd={!initialMedias && hasMore && !(isLoading || isLoadingMore) ? loadMore : undefined}
                    // onVisibleItemsChange={handleVisibleItemsChange}
                    emptyState={<ContentPageEmptyState title={l10n.content.noVideosAvailable} />}
                />

                {/* Loading More Indicator */}
                {!initialMedias && hasMore && (isLoading || isLoadingMore) && (
                    <div className="py-8 flex justify-center">
                        <AnimatedLoadingIndicator />
                    </div>
                )}

                {/* End of List */}
                {!hasMore && medias.length > 0 && (
                    <div className="py-8 text-center" style={{ color: theme.text.secondary }}>
                        <div className="text-sm">{l10n.common.reachedEnd}</div>
                    </div>
                )}
            </div>
        </div>
    );
};
